import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatErr } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { KeyRound, Copy } from "lucide-react";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [params] = useSearchParams();
  const [step, setStep] = useState(params.get("token") ? "reset" : "request");
  const [email, setEmail] = useState("");
  const [token, setToken] = useState(params.get("token") || "");
  const [newPassword, setNewPassword] = useState("");
  const [generatedToken, setGeneratedToken] = useState("");
  const [loading, setLoading] = useState(false);

  const request = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      if (data.token) {
        setGeneratedToken(data.token);
        setToken(data.token);
        toast.success("Reset kód vygenerován");
        setStep("reset");
      } else {
        toast.success(data.message || "Pokud účet existuje, byl vygenerován kód.");
      }
    } catch (err) {
      toast.error(formatErr(err));
    } finally { setLoading(false); }
  };

  const reset = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) { toast.error("Heslo musí mít alespoň 6 znaků"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, new_password: newPassword });
      localStorage.setItem("pranidomu_token", data.token);
      setUser(data.user);
      toast.success("Heslo úspěšně změněno");
      const target =
        data.user.role === "provider" ? "/dashboard/provider" :
        data.user.role === "admin" ? "/admin" :
        "/dashboard/customer";
      navigate(target);
    } catch (err) {
      toast.error(formatErr(err));
    } finally { setLoading(false); }
  };

  const copyToken = async () => {
    await navigator.clipboard.writeText(generatedToken);
    toast.success("Zkopírováno");
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      <main className="flex-1 container-page py-16 grid lg:grid-cols-2 gap-12 items-center">
        <div className="hidden lg:block">
          <div className="tiny-label mb-3">Zapomenuté heslo</div>
          <h1 className="font-display text-4xl xl:text-5xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
            Zapomněli jste heslo? Nic se neděje.
          </h1>
          <p className="mt-5 text-slate-600 max-w-md">
            Zadejte email a my vygenerujeme dočasný kód (60 minut). Pomocí něj si nastavíte nové heslo.
          </p>
        </div>

        <Card className="p-8 max-w-md w-full mx-auto">
          <div className="flex items-center gap-2 mb-1">
            <KeyRound className="w-5 h-5 text-sky-600" />
            <h2 className="font-display text-2xl font-bold text-slate-900">
              {step === "request" ? "Reset hesla" : "Nové heslo"}
            </h2>
          </div>
          {step === "request" ? (
            <>
              <p className="text-sm text-slate-500 mb-6">Vygenerujeme reset kód.</p>
              <form onSubmit={request} className="space-y-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" />
                </div>
                <Button type="submit" className="w-full" disabled={loading} data-testid="forgot-submit">
                  {loading ? "Odesílám…" : "Vygenerovat reset kód"}
                </Button>
              </form>
            </>
          ) : (
            <>
              {generatedToken && (
                <div className="mb-5 p-3 rounded-lg bg-amber-50 border border-amber-200">
                  <div className="text-xs text-amber-900 font-semibold uppercase tracking-wider mb-1">MVP režim</div>
                  <p className="text-xs text-amber-800">V produkci by tento kód přišel emailem. Pro MVP je zobrazen rovnou:</p>
                  <div className="mt-2 flex items-center gap-2 bg-white border border-amber-300 rounded px-2 py-1">
                    <code className="text-xs flex-1 break-all" data-testid="reset-token-display">{generatedToken}</code>
                    <button type="button" onClick={copyToken} className="text-amber-700 hover:text-amber-900" data-testid="reset-token-copy">
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
              <form onSubmit={reset} className="space-y-4">
                <div>
                  <Label htmlFor="token">Reset kód</Label>
                  <Input id="token" required value={token} onChange={(e) => setToken(e.target.value)} data-testid="reset-token" />
                </div>
                <div>
                  <Label htmlFor="newpwd">Nové heslo (min. 6 znaků)</Label>
                  <Input id="newpwd" type="password" required minLength={6} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} data-testid="reset-new-password" />
                </div>
                <Button type="submit" className="w-full" disabled={loading} data-testid="reset-submit">
                  {loading ? "Ukládám…" : "Nastavit nové heslo"}
                </Button>
              </form>
            </>
          )}
          <div className="mt-5 text-sm text-slate-600">
            <Link to="/prihlaseni" className="text-sky-600 font-semibold hover:underline">← Zpět na přihlášení</Link>
          </div>
        </Card>
      </main>
      <Footer />
    </div>
  );
};

export default ForgotPassword;
